// ─── Realtime Sync Conflicts Hook ────────────────────────────────────────────
//
// G2.18: Subscribes to the WebSocket `sync:conflict` event emitted by the
// backend (`services/api/src/events/events.gateway.ts` → `emitSyncConflict`)
// and fires a warning toast + refreshes the pending conflict count so the
// Sync nav badge updates in realtime.
//
// Scoping: only runs for MANAGER+ roles. Cashiers don't resolve conflicts,
// so we skip the subscription entirely rather than silently no-oping inside
// the handler. This avoids waking the notification store on every conflict
// for CASHIER terminals.
//
// Shared socket: this hook piggybacks on the socket connection opened by
// `useRealtimeNotifications` — it does NOT call `connectSocket` itself.
// `onSocketEvent` attaches to the existing socket (see socket-client.ts).
//
// Traceability: FR-SYNC-008, FR-SYNC-013, US-BILL-019.

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { useSyncStore } from '@/stores/use-sync-store';
import { onSocketEvent } from '@/lib/socket-client';
import { isManagerPlus, ENTITY_TYPE_CONFIG } from '@/lib/sync-constants';
import { handleConflictResolved, type SyncConflictResolvedPayload } from '@/services/conflict-resolved.handler';
import { onBillProblem } from '@/services/sync-queue.service';
import { plainSyncReason } from '@/lib/offline-bills';

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Shape of the `sync:conflict` event emitted by EventsGateway. Mirrors
 * services/api/src/events/events.gateway.ts :: emitSyncConflict.
 */
interface SyncConflictEventPayload {
  conflict_id: string;
  entity_type: string;
  entity_id?: string;
  message: string;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useSyncConflictsRealtime(): void {
  const profile = useAuthStore((s) => s.profile);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Refs so the effect body can read the latest references without
  // re-binding the socket listener on every render.
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    const tenantId = profile?.tenant?.id;
    const role = profile?.role;
    if (!tenantId || !role) return;

    // Scoping: only MANAGER+ see sync conflict toasts. Cashiers don't
    // resolve conflicts so they don't need the live surface.
    if (!isManagerPlus(role)) return;

    // No connectSocket() call — the notification hook owns the socket
    // lifecycle. We just attach a listener to whatever socket it opened.
    const unsubscribe = onSocketEvent<SyncConflictEventPayload>('sync:conflict', (payload) => {
      // Defensive: drop malformed events rather than crashing the listener.
      if (!payload || typeof payload.conflict_id !== 'string' || !payload.entity_type) return;

      // Refresh the server-side pending count so the nav badge updates.
      // Fire-and-forget — the store logs failures without surfacing them.
      void useSyncStore.getState().refreshConflictCount();

      const entityLabel =
        ENTITY_TYPE_CONFIG[payload.entity_type]?.label ?? payload.entity_type;

      toastRef.current({
        variant: 'warning',
        title: `Sync Conflict (${entityLabel})`,
        description: payload.message || 'A pending offline sync needs your review.',
        action: {
          label: 'Review',
          onClick: () => {
            navigateRef.current('/pos/sync?tab=conflicts');
          },
        },
      });
    });

    return () => {
      unsubscribe();
    };
  }, [profile?.tenant?.id, profile?.role]);

  // Every till, cashiers included: a resolution settles this till's stuck
  // queue row and may change a record it caches (sync plan Phase 5).
  useEffect(() => {
    const tenantId = profile?.tenant?.id;
    const role = profile?.role;
    if (!tenantId || !role) return;
    return onSocketEvent<SyncConflictResolvedPayload>('sync:conflict:resolved', (payload) => {
      void handleConflictResolved(payload).catch(() => undefined);
      if (isManagerPlus(role)) void useSyncStore.getState().refreshConflictCount();
    });
  }, [profile?.tenant?.id, profile?.role]);

  // T19: every till, cashiers included — one of this till's own offline bills
  // was refused (needs review) or ran out of tries. Read from the push result,
  // so it needs no socket and also reaches a till that just reconnected.
  useEffect(() => {
    if (!profile?.tenant?.id) return;
    return onBillProblem((problem) => {
      const bill = problem.offline_number ? `Offline bill ${problem.offline_number}` : 'An offline bill';
      toastRef.current({
        variant: problem.status === 'CONFLICT' ? 'warning' : 'error',
        title: problem.status === 'CONFLICT' ? `${bill} needs review` : `${bill} did not sync`,
        description:
          plainSyncReason({ status: problem.status, last_error: problem.reason }) ?? problem.reason,
        action: {
          label: 'View',
          onClick: () => {
            navigateRef.current('/pos/sync');
          },
        },
      });
    });
  }, [profile?.tenant?.id]);
}
